<script lang="ts">
import { InstrumentState } from '~/models/instruments';
</script>

<script setup lang="ts">
import SkeletonInstrumentCard from '~/components/instruments/SkeletonInstrumentCard.vue';
import InstrumentCard from '~/components/instruments/InstrumentCard.vue';

const { locale } = useI18n();
const localePath = useLocalePath();
const instrumentsStore = useInstrumentsStore();
const { loadingInstruments, instruments } = storeToRefs(instrumentsStore);

const isShowingMore = ref(false);
const isMedium = useMedia({ query: '(min-width: 768px) and (max-width: 1199.98px)' });
const numberOfInstrumentsToShow = computed(() => {
  if (isMedium.value) {
    if (isShowingMore.value) {
      return 4;
    }

    return 2;
  }

  if (isShowingMore.value) {
    return 6;
  }

  return 3;
});
const skeletonInstrumentsToShow = computed(() =>
  Array.from({ length: numberOfInstrumentsToShow.value }, (_, i) => i),
);

const previewInstruments = computed(() =>
  instrumentsStore.instruments.slice(0, numberOfInstrumentsToShow.value),
);

watch(
  locale,
  async () => {
    await instrumentsStore.getInstruments({});

    if (
      instruments.value.filter(
        (instrument) =>
          instrument.State === InstrumentState.InPreparation ||
          instrument.State === InstrumentState.BeingSubscribed,
      ).length > 3
    ) {
      isShowingMore.value = true;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="container">
    <div class="row">
      <template v-if="loadingInstruments || previewInstruments.length === 0">
        <div v-for="i in skeletonInstrumentsToShow" :key="i" class="col">
          <SkeletonInstrumentCard />
        </div>
      </template>

      <div v-for="instrument in previewInstruments" v-else :key="instrument.Id" class="col">
        <InstrumentCard :instrument="instrument" />
      </div>
    </div>

    <div class="row">
      <div class="col col--show-more">
        <Button
          as="NuxtLink"
          variant="outlined"
          class="show-all-instruments"
          :to="localePath('investments-offer')"
        >
          {{ $t('homepage.instruments-preview.show-all-instruments') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.row {
  display: grid;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.col--show-more {
  display: flex;
  justify-content: center;
  grid-column: span 4;

  .show-all-instruments {
    margin-top: 2.5rem;
  }
}
</style>
